import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DAYS, IDaySchedule, openStatus, rangesLabel, todayIndex } from '../../../core/utils/schedule.util';

export interface IPreviewSocial { code?: string; name?: string; value: string; }

/** Ficha del negocio tal como la ven los clientes en la app (vista previa en vivo de Agregar negocio y Ajustes). */
@Component({
  selector: 'app-business-preview',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article class="ficha" [class.is-compact]="!showSchedule() && !showSocial()">
      <div class="cover">
        <app-business-logo [name]="displayName()" [url]="horizontalLogoUrl()" format="horizontal" [size]="56" [radius]="14" />
      </div>
      <div class="titles">
        <h3>{{ displayName() }}</h3>
        <p>{{ subtitle() }}</p>
      </div>
      @if (showSchedule() && days()) {
        <span class="status" [class.is-open]="status().open"><i></i>{{ status().label }}</span>
        <div class="block">
          <p class="cr-eyebrow cr-text-tertiary">Horario</p>
          <ul class="sched">
            @for (d of rows(); track d.day) {
              <li [class.is-today]="d.day === today()"><span>{{ d.label }}</span><span>{{ d.value }}</span></li>
            }
          </ul>
        </div>
      }
      @if (showSocial()) {
        <div class="block">
          <p class="cr-eyebrow cr-text-tertiary">Redes</p>
          @if (socials().length) {
            <ul class="social">
              @for (s of socials(); track $index) {
                <li><app-social-glyph [code]="s.code" [size]="16" [tile]="true" [tileSize]="32" /><span>{{ shortValue(s) }}</span></li>
              }
            </ul>
          } @else { <p class="none">Sin redes por ahora</p> }
        </div>
      }
    </article>
  `,
  styles: [`
    .ficha{background:#fff;border-radius:28px;padding:28px;box-shadow:var(--cr-elevation-2);display:flex;flex-direction:column;gap:16px;width:100%}
    .ficha.is-compact{padding:16px;border-radius:24px;gap:14px}
    .cover{height:104px;border-radius:18px;background:var(--cr-brand-teal-soft);display:grid;place-items:center;overflow:hidden}
    .is-compact .cover{height:96px;border-radius:16px}
    .titles h3{margin:0;font:700 22px/26px var(--cr-font-display);letter-spacing:-.2px}
    .titles p{margin:4px 0 0;font:400 14px/20px var(--cr-font-body);color:var(--cr-text-tertiary)}
    .status{align-self:flex-start;display:inline-flex;align-items:center;gap:8px;padding:6px 12px;border-radius:999px;background:var(--cr-bg-surface-2);font:600 13px/18px var(--cr-font-body);color:var(--cr-text-secondary)}
    .status i{width:8px;height:8px;border-radius:50%;background:var(--cr-text-tertiary)}
    .status.is-open{background:var(--cr-success-soft);color:var(--cr-success)}
    .status.is-open i{background:var(--cr-success)}
    .block{display:flex;flex-direction:column;gap:10px;border-top:1px solid var(--cr-border-subtle);padding-top:16px}
    .block p{margin:0}
    .sched,.social{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px}
    .sched li{display:flex;justify-content:space-between;gap:12px;font:400 14px/20px var(--cr-font-body);color:var(--cr-text-secondary)}
    .sched li span:last-child{text-align:right}
    .sched li.is-today{font-weight:700;color:var(--cr-text-primary)}
    .social li{display:flex;align-items:center;gap:12px;font:400 15px/22px var(--cr-font-body)}
    .none{font:400 14px/20px var(--cr-font-body);color:var(--cr-text-tertiary)}
    @media (max-width:640px){ .ficha{padding:20px;border-radius:24px} }
  `],
})
export class BusinessPreviewComponent {
  readonly name = input<string>('');
  readonly categoryName = input<string | null | undefined>('');
  readonly neighborhood = input<string | null | undefined>('');
  readonly horizontalLogoUrl = input<string | null | undefined>(null);
  readonly days = input<IDaySchedule[] | null>(null);
  readonly timeZone = input<string>('America/Mexico_City');
  readonly socials = input<IPreviewSocial[]>([]);
  readonly showSchedule = input<boolean>(false);
  readonly showSocial = input<boolean>(false);

  readonly displayName = computed(() => this.name().trim() || 'Tu negocio');
  readonly subtitle = computed(() => [this.categoryName() || 'Sin categoría', this.neighborhood()].filter(Boolean).join(' · '));
  readonly status = computed(() => openStatus(this.days() ?? [], this.timeZone()));
  readonly today = computed(() => todayIndex(this.timeZone()));
  readonly rows = computed(() => (this.days() ?? []).map(d => ({
    day: d.day,
    label: DAYS[d.day - 1].label,
    value: d.open ? rangesLabel(d.ranges) : 'Cerrado',
  })));

  shortValue(s: IPreviewSocial): string {
    const v = (s.value || '').trim();
    if (s.code === 'whatsapp') return v.replace(/^(\d{2})(\d{4})(\d{4})$/, '+52 $1 $2 $3');
    if (s.code === 'web') return v.replace(/^https?:\/\//, '');
    return v.startsWith('@') ? v : `@${v}`;
  }
}
